// The words of Moon reminders, and the mending of reminders saved before, shared by the app and the Worker (which
// bundles this file). Wherever a saved reminder is read, it is put right: those of the first sky calendar (1 October
// 2026) kept their place only in their link and called every phase of the Moon a supermoon rising at sunset; those
// made before 10 October 2026 said what was to come without "in shāʾ Allah", and the crescent's gave a verdict on
// whether it could be seen; the New Moon's said "New Moon" (often on the 29th, when Saudi Arabia looks for the crescent)
// and, until 14 October 2026, that the Moon was too close to the Sun to see.

/** Said of anything an alert tells of that is still to come (BUILD-SPEC item 20), after when: "Tonight, in shāʾ Allah: …". */
export const IA = 'in shāʾ Allah';

/** A reminder's title at most, as the app cuts it and the Worker takes it: room for "Tonight, in shāʾ Allah: look for
 *  the crescent of Jumādá al-Ākhirah". */
export const TITLE_MAX = 72;

/** Each phase of the Moon as a reminder says it (times are added by the app where it can work them out). */
export const MOON_WORDS = {
  newTitle: `Tonight, ${IA}: the darkest skies of the month`,
  newBody: 'Faint stars and the Milky Way show at their best. Find a dark spot and give your eyes 20 minutes.',
  firstTitle: `Tonight, ${IA}: first quarter Moon`,
  lastTitle: `Before dawn, ${IA}: last quarter Moon`,
  fullTitle: `Tonight, ${IA}: Full Moon`,
  superTitle: `Tonight, ${IA}: a supermoon`,
  half: 'Half of the Moon’s face is lit.',
  craters: ' Binoculars show craters along the edge of the shadow.',
  fullBody: 'The whole of the Moon’s face is lit.',
};

/** A reminder's place from its own Ahillah link, to about 10 km; null if the link gives none. */
export function placeFromLink(url: string): { lat: number; lon: number } | null {
  if (!url.startsWith('/') || url.startsWith('//')) return null;
  const q = new URL(url, 'https://ahillah.invalid').searchParams;
  const a = q.get('lat'), b = q.get('lon');
  if (!a || !b) return null;
  const lat = Number(a), lon = Number(b);
  if (!Number.isFinite(lat) || !Number.isFinite(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
  return { lat: Math.round(lat * 10) / 10, lon: Math.round(lon * 10) / 10 };
}

/** How the first sky calendar ended every Moon reminder, and titled them. */
const OLD_MOON_END = ' Watch it rise in the east around sunset.';
const OLD_SUPER_TITLE = 'Tonight: a supermoon';
/** The time a reminder's title began with before "in shāʾ Allah" followed it. */
const OLD_WHEN = /^(Tonight|Tomorrow|Before dawn|Today|This evening|In an hour|Soon): /;
/** How the crescent's reminder ended when it still predicted whether the crescent could be seen. */
const OLD_CRESCENT_END = 'A guide for sighting, not a replacement for it.';
/** The New Moon's reminder as it was titled, with or without "in shāʾ Allah". */
const OLD_NEW_TITLE = /^Tonight(?:, in shāʾ Allah)?: New Moon, with no moonlight$/;

/** A reminder as it should be: its place from its link if it has none of its own, its Moon words by phase, no verdict on the
 *  crescent, and "in shāʾ Allah" after when. */
export function mendReminder<T extends { title: string; body: string; url: string; lat?: number; lon?: number }>(r: T): T {
  let out = r;
  if (out.lat == null && out.lon == null) {
    const p = placeFromLink(out.url);
    if (p) out = { ...out, ...p };
  }
  if (out.title === OLD_SUPER_TITLE && out.body.endsWith(OLD_MOON_END)) {
    const q = /[?&]ev=moon-(\d)-/.exec(out.url)?.[1];
    const detail = out.body.slice(0, -OLD_MOON_END.length);
    const w = MOON_WORDS;
    const [title, body] =
      q === '0' ? [w.newTitle, w.newBody]
      : q === '1' ? [w.firstTitle, `${w.half} It is in the evening sky.${w.craters}`]
      : q === '3' ? [w.lastTitle, `${w.half} It rises around midnight and stays up into the morning.${w.craters}`]
      // A Full Moon: a supermoon only if its own words said so.
      : [/supermoon/i.test(detail) ? w.superTitle : w.fullTitle, `${detail} It rises in the east around sunset.`];
    out = { ...out, title, body };
  }
  // The crescent's old reminder, with its verdict and time: only where to look remains, and it says to look for it, so it
  // cannot read as a prediction (owner, 14 October 2026).
  const crescent = /^This evening: the crescent of (.+?)(?: \d{4})?$/.exec(out.title);
  if (crescent && out.body.endsWith(OLD_CRESCENT_END)) out = { ...out, title: `Tonight, ${IA}: look for the crescent of ${crescent[1]}`, body: 'Look low in the west just after sunset.' };
  const named = /^Tonight, in shāʾ Allah: the crescent of (.+)$/.exec(out.title);
  if (named) out = { ...out, title: `Tonight, ${IA}: look for the crescent of ${named[1]}` };
  // The New Moon's: no "New Moon", and nothing on whether the Moon can be seen.
  if (OLD_NEW_TITLE.test(out.title)) out = { ...out, title: MOON_WORDS.newTitle, body: MOON_WORDS.newBody };
  if (OLD_WHEN.test(out.title)) out = { ...out, title: out.title.replace(OLD_WHEN, `$1, ${IA}: `) };
  return out;
}
